import { cn, colors } from '@noorden-insurtech/alba-design-system';
import type { ReactNode } from 'react';
import { Cell, Pie, PieChart } from 'recharts';

export interface DonutChartSegment {
  key: string;
  label: string;
  value: number;
  /** Cor de preenchimento (hex/css) — use os tokens `colors` do design system. */
  color: string;
}

export interface DonutChartProps {
  segments: DonutChartSegment[];
  /**
   * Diâmetro em px. Tamanho fixo de propósito: dentro de uma aba oculta o
   * `ResponsiveContainer` mede 0×0 e o gráfico some.
   */
  size?: number;
  /** Espessura do anel em px. */
  thickness?: number;
  /** Conteúdo exibido no centro do anel (ex.: o total). */
  center?: ReactNode;
  /** Cor do anel quando todos os valores são zero. */
  emptyColor?: string;
  ariaLabel?: string;
  className?: string;
}

const EMPTY_KEY = '__empty__';

/**
 * Anel (donut) genérico e burro: recebe os segmentos prontos e só desenha.
 * Legenda e tooltip ficam com quem usa — o componente não sabe o que os
 * números significam.
 */
export function DonutChart({
  segments,
  size = 96,
  thickness = 12,
  center,
  emptyColor = colors['alba-gray-200'],
  ariaLabel,
  className,
}: DonutChartProps) {
  const visible = segments.filter(segment => segment.value > 0);
  const data = visible.length
    ? visible
    : [{ key: EMPTY_KEY, label: 'Vazio', value: 1, color: emptyColor }];

  const outerRadius = size / 2;
  const innerRadius = Math.max(outerRadius - thickness, 0);

  return (
    <div
      role='img'
      aria-label={ariaLabel}
      className={cn('relative shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <PieChart width={size} height={size}>
        <Pie
          data={data}
          dataKey='value'
          nameKey='label'
          cx='50%'
          cy='50%'
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={90}
          endAngle={-270}
          stroke='none'
          isAnimationActive={false}
        >
          {data.map(segment => (
            <Cell key={segment.key} fill={segment.color} />
          ))}
        </Pie>
      </PieChart>
      {center !== undefined && (
        <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
          {center}
        </div>
      )}
    </div>
  );
}
